import { motion } from "framer-motion";
import { Phone, BookOpen } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { WHATSAPP_URL, track, scrollToSection } from "@/lib/api";

export function Reveal({ children, delay = 0, className = "", ...props }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 26 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.65, delay, ease: [0.22, 1, 0.36, 1] }}
      {...props}
    >
      {children}
    </motion.div>
  );
}

export function SectionHead({ overline, title, sub, dark = false, align = "center" }) {
  const alignCls = align === "center" ? "text-center mx-auto" : "text-left";
  return (
    <Reveal className={`max-w-3xl ${alignCls} mb-12 lg:mb-16`}>
      {overline && (
        <p className={`text-xs font-data uppercase tracking-[0.25em] font-semibold mb-4 ${dark ? "text-sky-300" : "text-[#1E40AF]"}`}>
          {overline}
        </p>
      )}
      <h2 className={`text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight leading-tight ${dark ? "text-white" : "text-[#0B192C]"}`}>
        {title}
      </h2>
      {sub && (
        <p className={`mt-4 text-base sm:text-lg leading-relaxed ${dark ? "text-slate-300" : "text-slate-600"}`}>{sub}</p>
      )}
    </Reveal>
  );
}

const MARQUEE_ITEMS = [
  "STUDY IN ROMANIA",
  "UNITED KINGDOM • GERMANY • ITALY",
  "FRANCE • IRELAND",
  "ENGLISH-TAUGHT PROGRAMS",
  "BACHELOR'S & MASTER'S",
  "MEDICINE • ENGINEERING • BUSINESS",
  "SCHOLARSHIP GUIDANCE",
  "COMPLETE APPLICATION SUPPORT",
];

export function Marquee() {
  const row = [...MARQUEE_ITEMS, ...MARQUEE_ITEMS];
  return (
    <div data-testid="editorial-marquee" className="bg-white border-y border-slate-200 py-4 overflow-hidden">
      <div className="flex whitespace-nowrap animate-marquee w-max">
        {[0, 1].map((half) => (
          <div key={half} className="flex items-center">
            {row.map((item, i) => (
              <span key={`${half}-${i}`} className="flex items-center">
                <span className="font-data text-xs sm:text-sm tracking-[0.25em] text-slate-500 uppercase px-6">{item}</span>
                <span className="text-[#B45309] text-xs">✦</span>
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export function WhatsAppFloat() {
  return (
    <a
      href={WHATSAPP_URL}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => track("whatsapp_click", { source: "floating_button" })}
      data-testid="whatsapp-floating-trigger"
      className="group fixed bottom-20 lg:bottom-6 right-4 lg:right-6 z-50 flex items-center"
      aria-label="Chat with a Counsellor on WhatsApp"
    >
      <span className="max-w-0 overflow-hidden group-hover:max-w-[220px] transition-all duration-500 ease-out">
        <span className="whitespace-nowrap mr-3 bg-[#0B192C] text-white text-xs font-semibold px-4 py-2.5 rounded-full shadow-xl">
          Chat with a Counsellor
        </span>
      </span>
      <span className="relative pulse-ring flex items-center justify-center w-14 h-14 rounded-full bg-[#059669] shadow-2xl shadow-emerald-900/30 group-hover:scale-105 transition-transform">
        <svg viewBox="0 0 24 24" className="w-7 h-7 fill-white" aria-hidden="true">
          <path d="M17.5 14.4c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.96-.94 1.16-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.14-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.5 0 1.47 1.07 2.9 1.22 3.1.15.2 2.11 3.22 5.1 4.51.71.31 1.27.49 1.7.63.72.23 1.37.2 1.88.12.57-.09 1.76-.72 2.01-1.42.25-.7.25-1.29.17-1.42-.07-.13-.27-.2-.57-.35M12.05 21.79h-.01a9.86 9.86 0 0 1-5.03-1.38l-.36-.21-3.74.98 1-3.65-.24-.37a9.82 9.82 0 0 1-1.51-5.26c0-5.44 4.43-9.87 9.88-9.87a9.8 9.8 0 0 1 6.99 2.9 9.8 9.8 0 0 1 2.89 6.97c0 5.45-4.43 9.88-9.87 9.88m8.41-18.28A11.8 11.8 0 0 0 12.04 0C5.5 0 .16 5.34.16 11.89c0 2.1.55 4.14 1.59 5.95L.06 24l6.3-1.65a11.9 11.9 0 0 0 5.68 1.45h.01c6.55 0 11.89-5.34 11.89-11.89 0-3.18-1.24-6.16-3.48-8.4" />
        </svg>
      </span>
    </a>
  );
}

export function FloatingBookCTA() {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => { track("floating_cta_click"); navigate("/contact"); }}
      data-testid="floating-book-counselling"
      className="hidden lg:flex fixed left-6 bottom-6 z-50 items-center gap-2.5 bg-white border border-slate-200 shadow-xl shadow-slate-900/10 rounded-full pl-4 pr-5 py-3 text-[13px] font-bold text-[#0B192C] hover:border-[#1E40AF] hover:text-[#1E40AF] active:scale-[0.98] transition-all"
    >
      <span className="w-8 h-8 rounded-full bg-[#1E40AF] flex items-center justify-center">
        <BookOpen className="w-4 h-4 text-white" />
      </span>
      BOOK COUNSELLING
    </button>
  );
}

export function MobileCTA() {
  const navigate = useNavigate();
  return (
    <div data-testid="mobile-sticky-cta" className="fixed bottom-0 inset-x-0 z-40 lg:hidden grid grid-cols-3 border-t border-slate-200 bg-white shadow-[0_-4px_20px_rgba(7,14,30,0.08)]">
      <a
        href="tel:+911204615329"
        onClick={() => track("phone_click", { source: "mobile_bar" })}
        data-testid="mobile-cta-call"
        className="flex items-center justify-center gap-1.5 py-3.5 text-[11px] font-bold text-[#0B192C]"
      >
        <Phone className="w-4 h-4" /> CALL NOW
      </a>
      <a
        href={WHATSAPP_URL}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => track("whatsapp_click", { source: "mobile_bar" })}
        data-testid="mobile-cta-whatsapp"
        className="flex items-center justify-center py-3.5 text-[11px] font-bold text-white bg-[#059669]"
      >
        WHATSAPP
      </a>
      <button
        onClick={() => navigate("/contact")}
        data-testid="mobile-cta-counselling"
        className="py-3.5 text-[11px] font-bold text-white bg-[#1E40AF]"
      >
        PROFILE ASSESSMENT
      </button>
    </div>
  );
}
